import JWPlayer from '@jwplayer/jwplayer-react';
import {
    MutableRefObject,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { useAnalytics } from './useAnalytics';

export interface ICaptionsList {
  track?: number;
  tracks?: [] | undefined;
}

interface ICaptionsProps {
  playerRef: MutableRefObject<typeof JWPlayer>;
  enabled?: boolean;
}

export const useCaptions = (props: ICaptionsProps) => {
    const { playerRef, enabled = true } = props;

    const [captions, setCaptions] = useState<ICaptionsList | null>(null);
    const { pushToDataLayer } = useAnalytics();

    const handleCaptionsList = (c: ICaptionsList) => {
        setCaptions({ ...c, tracks: c.tracks });
    };

    const handleCaptionsChanged = (c: ICaptionsList) => {
        setCaptions({ ...c, track: c.track });
    };

    const onToggleCaptions = useCallback(() => {
        if (playerRef.current) {
            const currentTrack = playerRef.current.getCurrentCaptions() || 0;

            if (currentTrack === 0) {
                pushToDataLayer({ event: 'player_cc' });
            }
            
            const newTrack = currentTrack === 0 ? 1 : 0;
            playerRef.current.setCurrentCaptions(newTrack);
            
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playerRef, captions?.track]);

    const handleCaptionsKeydown = useCallback((e: Event) => {
        e.stopPropagation();
        const key = (e as KeyboardEvent).key;
        if (key === ' ' || key === 'Enter') {
            const ccButton = document.querySelector('.pli-icon-cc') as HTMLDivElement;
            ccButton.focus();
        }
    }, []);

    const ccButtonSvg = useMemo(() => {
        return '<svg xmlns="http://www.w3.org/2000/svg" xml:space="preserve" id="Layer_1" x="0" y="0" class="jw-svg-icon jw-svg-icon-cc" style="enable-background:new 0 0 24 24" version="1.1" viewBox="0 0 24 24"><style>.st3{fill-rule:evenodd;clip-rule:evenodd;fill:#fff}</style><path d="M18.3 13c0 .5-.1.9-.3 1.2-.2.3-.4.7-.6.9-.3.3-.6.5-.9.6-.4.1-.8.2-1.2.2-.5 0-1-.1-1.4-.3-.4-.2-.8-.5-1.1-.8-.3-.3-.5-.7-.7-1.2-.2-.4-.2-.9-.2-1.4 0-.5.1-1 .2-1.4.2-.4.4-.8.7-1.2.3-.3.6-.6 1.1-.8.4-.2.9-.3 1.4-.3.4 0 .8.1 1.1.2.4.1.7.3.9.5.3.2.5.5.7.8.2.3.3.6.3 1v.1h-1.4V11c0-.2-.1-.4-.2-.5-.1-.2-.2-.3-.3-.4-.1-.1-.3-.2-.5-.3-.2-.1-.4-.1-.6-.1-.3 0-.7.1-.9.2-.3.1-.5.3-.6.6-.2.2-.3.5-.4.8-.1.3-.1.6-.1.9 0 .3 0 .6.1.9.1.3.2.6.4.8l.6.6c.3.1.6.2.9.2.3 0 .5 0 .7-.1.2-.1.4-.2.5-.4.1-.2.2-.3.3-.5.1-.2.1-.4.2-.7v-.1h1.3zm-8.4 0h1.3v.1c0 .4-.1.8-.3 1.2-.2.3-.4.7-.6.9-.3.3-.6.5-.9.6-.3.1-.7.2-1.2.2s-1-.1-1.4-.3c-.4-.2-.8-.5-1.1-.8-.3-.3-.5-.7-.7-1.2-.2-.4-.2-.9-.2-1.4 0-.5.1-1 .2-1.4.2-.4.4-.8.7-1.2.3-.3.6-.6 1.1-.8.4-.2.9-.3 1.4-.3.4 0 .8.1 1.1.2.4.1.7.3.9.5.3.2.5.5.7.8.2.3.3.6.3 1v.1H9.9v-.1c0-.2-.1-.4-.2-.5-.1-.2-.2-.3-.3-.4-.2-.2-.3-.2-.5-.3-.2-.1-.4-.1-.7-.1-.3 0-.7.1-.9.2-.3.1-.5.3-.6.6-.2.2-.3.5-.4.8-.1.3-.1.6-.1.9 0 .3 0 .6.1.9.1.3.2.6.4.8l.6.6c.2.1.6.2.9.2.3 0 .5 0 .7-.1.2-.1.4-.2.5-.4.1-.2.3-.3.3-.5.1-.2.2-.4.2-.8z" style="fill-rule:evenodd;clip-rule:evenodd"/><rect x="3" y="5.8" style="fill:none;stroke:#fff;stroke-width:2"/><path d="M18.3 13c0 .5-.1.9-.3 1.2-.2.3-.4.7-.6.9-.3.3-.6.5-.9.6-.4.1-.8.2-1.2.2-.5 0-1-.1-1.4-.3-.4-.2-.8-.5-1.1-.8-.3-.3-.5-.7-.7-1.2-.2-.4-.2-.9-.2-1.4 0-.5.1-1 .2-1.4.2-.4.4-.8.7-1.2.3-.3.6-.6 1.1-.8.4-.2.9-.3 1.4-.3.4 0 .8.1 1.1.2.4.1.7.3.9.5.3.2.5.5.7.8.2.3.3.6.3 1v.1h-1.4V11c0-.2-.1-.4-.2-.5-.1-.2-.2-.3-.3-.4-.1-.1-.3-.2-.5-.3-.2-.1-.4-.1-.6-.1-.3 0-.7.1-.9.2-.3.1-.5.3-.6.6-.2.2-.3.5-.4.8-.1.3-.1.6-.1.9 0 .3 0 .6.1.9.1.3.2.6.4.8l.6.6c.3.1.6.2.9.2.3 0 .5 0 .7-.1.2-.1.4-.2.5-.4.1-.2.2-.3.3-.5.1-.2.1-.4.2-.7v-.1h1.3zm-8.4 0h1.3v.1c0 .4-.1.8-.3 1.2-.2.3-.4.7-.6.9-.3.3-.6.5-.9.6-.3.1-.7.2-1.2.2s-1-.1-1.4-.3c-.4-.2-.8-.5-1.1-.8-.3-.3-.5-.7-.7-1.2-.2-.4-.2-.9-.2-1.4 0-.5.1-1 .2-1.4.2-.4.4-.8.7-1.2.3-.3.6-.6 1.1-.8.4-.2.9-.3 1.4-.3.4 0 .8.1 1.1.2.4.1.7.3.9.5.3.2.5.5.7.8.2.3.3.6.3 1v.1H9.9v-.1c0-.2-.1-.4-.2-.5-.1-.2-.2-.3-.3-.4-.2-.2-.3-.2-.5-.3-.2-.1-.4-.1-.7-.1-.3 0-.7.1-.9.2-.3.1-.5.3-.6.6-.2.2-.3.5-.4.8-.1.3-.1.6-.1.9 0 .3 0 .6.1.9.1.3.2.6.4.8l.6.6c.2.1.6.2.9.2.3 0 .5 0 .7-.1.2-.1.4-.2.5-.4.1-.2.3-.3.3-.5.1-.2.2-.4.2-.8z" class="st3"/><path d="M21.2 20.2H2V4.8h19.2v15.4zM4 18.2h15.2V6.8H4v11.4z" style="fill:#fff"/><path d="M18.3 13c0 .5-.1.9-.3 1.2-.2.3-.4.7-.6.9-.3.3-.6.5-.9.6-.4.1-.8.2-1.2.2-.5 0-1-.1-1.4-.3-.4-.2-.8-.5-1.1-.8-.3-.3-.5-.7-.7-1.2-.2-.4-.2-.9-.2-1.4 0-.5.1-1 .2-1.4.2-.4.4-.8.7-1.2.3-.3.6-.6 1.1-.8.4-.2.9-.3 1.4-.3.4 0 .8.1 1.1.2.4.1.7.3.9.5.3.2.5.5.7.8.2.3.3.6.3 1v.1h-1.4V11c0-.2-.1-.4-.2-.5-.1-.2-.2-.3-.3-.4-.1-.1-.3-.2-.5-.3-.2-.1-.4-.1-.6-.1-.3 0-.7.1-.9.2-.3.1-.5.3-.6.6-.2.2-.3.5-.4.8-.1.3-.1.6-.1.9 0 .3 0 .6.1.9.1.3.2.6.4.8l.6.6c.3.1.6.2.9.2.3 0 .5 0 .7-.1.2-.1.4-.2.5-.4.1-.2.2-.3.3-.5.1-.2.1-.4.2-.7v-.1h1.3zm-8.4 0h1.3v.1c0 .4-.1.8-.3 1.2-.2.3-.4.7-.6.9-.3.3-.6.5-.9.6-.3.1-.7.2-1.2.2s-1-.1-1.4-.3c-.4-.2-.8-.5-1.1-.8-.3-.3-.5-.7-.7-1.2-.2-.4-.2-.9-.2-1.4 0-.5.1-1 .2-1.4.2-.4.4-.8.7-1.2.3-.3.6-.6 1.1-.8.4-.2.9-.3 1.4-.3.4 0 .8.1 1.1.2.4.1.7.3.9.5.3.2.5.5.7.8.2.3.3.6.3 1v.1H9.9v-.1c0-.2-.1-.4-.2-.5-.1-.2-.2-.3-.3-.4-.2-.2-.3-.2-.5-.3-.2-.1-.4-.1-.7-.1-.3 0-.7.1-.9.2-.3.1-.5.3-.6.6-.2.2-.3.5-.4.8-.1.3-.1.6-.1.9 0 .3 0 .6.1.9.1.3.2.6.4.8l.6.6c.2.1.6.2.9.2.3 0 .5 0 .7-.1.2-.1.4-.2.5-.4.1-.2.3-.3.3-.5.1-.2.2-.4.2-.8z" class="st3"/></svg>';
    }, []);


    const displayToggleCCButton = useCallback(
        (isEnabled: boolean | undefined) => {
            if (playerRef.current) {
                const buttonId = 'toggleCCButton';
                const track = playerRef.current.getCurrentCaptions() || 0;

                playerRef.current.addButton(
                    ccButtonSvg,
                    isEnabled
                        ? `Turn ${track === 0 ? 'on' : 'off'} closed captions`
                        : null,
                    isEnabled ? onToggleCaptions : null,
                    buttonId,
                    `pli-icon-cc ${isEnabled
                        ? track === 0
                            ? ''
                            : 'pli-icon-cc--on'
                        : 'pli-icon-cc--disabled'
                    }`
                );

                const ccButton = document.querySelector(
                    '.pli-icon-cc'
                ) as HTMLDivElement;

                if (ccButton) {
                    ccButton.addEventListener('keydown', handleCaptionsKeydown);
                }
            }
        },
        [ccButtonSvg, handleCaptionsKeydown, onToggleCaptions, playerRef]
    );

    useEffect(() => {
        if (enabled) {
            displayToggleCCButton(captions?.tracks && captions?.tracks.length > 1);
        }
    }, [captions, displayToggleCCButton, enabled]);

    return {
        handleCaptionsList,
        handleCaptionsChanged,
        captions,
        onToggleCaptions,
        displayToggleCCButton,
        captionsButtonIcon: ccButtonSvg,
    };
};
